import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Form as BootstrapForm } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import CollapsibleCard from '@spark/ui-components/components/CollapsibleCard';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import {
  getPremiacaoTabelaById,
  createPremiacaoTabela,
  updatePremiacaoTabela,
  deletePremiacaoTabela,
  createPremiacaoTabelaVeiculo,
  deletePremiacaoTabelaVeiculo
} from '@spark/services/premiacao';
import { tabelaPremiacaoFilterConfig } from '../config/tabelaPremiacaoFilterConfig';
import {
  applyOptionsToFilters,
  mapValuesToSelectOptions
} from '../utils/premiacaoMultiSelectFilters';
import { getTabelaPremiacaoColumns } from '../config/tabelaPremiacaoColumns';
import { getTabelaVeiculoVinculoColumns } from '../config/tabelaVeiculoVinculoColumns';
import {
  emptyTabelaPremiacaoForm,
  tabelaPremiacaoFormSections,
  tabelaPremiacaoFormStructure,
  validateTabelaPremiacaoForm
} from '../config/tabelaPremiacaoFormConfig';
import { usePremiacaoTabelaSelection } from '../hooks/usePremiacaoTabelaSelection';
import { usePremiacaoTabelaVeiculosVinculados } from '../hooks/usePremiacaoTabelaVeiculosVinculados';
import PremiacaoImportacaoExcelModal from './PremiacaoImportacaoExcelModal';
import PremiacaoVeiculoProprioSelectionModal from './PremiacaoVeiculoProprioSelectionModal';
import { importarPremiacaoTabelas, exportarLayoutImportacaoPremiacaoTabelasComRegistros } from '@spark/services/premiacao';
import { LAYOUT_IMPORTACAO_TABELAS } from '../config/premiacaoImportLayouts';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toRowModel = (item) => ({
  id: pick(item, 'id', 'Id'),
  nome: pick(item, 'nome', 'Nome') || '',
  segmento: pick(item, 'segmento', 'Segmento') || '',
  valorInicioFaturamento: pick(item, 'valorInicioFaturamento', 'ValorInicioFaturamento'),
  valorFinalFaturamento: pick(item, 'valorFinalFaturamento', 'ValorFinalFaturamento'),
  mediaIdeal: pick(item, 'mediaIdeal', 'MediaIdeal'),
  mediaExcelente: pick(item, 'mediaExcelente', 'MediaExcelente'),
  porcentagemDesconto: pick(item, 'porcentagemDesconto', 'PorcentagemDesconto'),
  valorPremiacao: pick(item, 'valorPremiacao', 'ValorPremiacao'),
  valorPremiacaoExcelente: pick(item, 'valorPremiacaoExcelente', 'ValorPremiacaoExcelente'),
  ativo: pick(item, 'ativo', 'Ativo') === true
});

const mapApiToFormValues = (data) => ({
  id: pick(data, 'id', 'Id') ?? '',
  nome: pick(data, 'nome', 'Nome') || '',
  segmento: pick(data, 'segmento', 'Segmento') || '',
  valorInicioFaturamento: pick(data, 'valorInicioFaturamento', 'ValorInicioFaturamento') ?? '',
  valorFinalFaturamento: pick(data, 'valorFinalFaturamento', 'ValorFinalFaturamento') ?? '',
  mediaIdeal: pick(data, 'mediaIdeal', 'MediaIdeal') ?? '',
  mediaExcelente: pick(data, 'mediaExcelente', 'MediaExcelente') ?? '',
  porcentagemDesconto: pick(data, 'porcentagemDesconto', 'PorcentagemDesconto') ?? '',
  valorPremiacao: pick(data, 'valorPremiacao', 'ValorPremiacao') ?? '',
  valorPremiacaoExcelente: pick(data, 'valorPremiacaoExcelente', 'ValorPremiacaoExcelente') ?? '',
  ativo: pick(data, 'ativo', 'Ativo') === true
});

const buildPayload = (values) => ({
  Nome: normalizeText(values.nome) ?? '',
  Segmento: normalizeText(values.segmento),
  ValorInicioFaturamento: parseNumber(values.valorInicioFaturamento) ?? 0,
  ValorFinalFaturamento: parseNumber(values.valorFinalFaturamento) ?? 0,
  MediaIdeal: parseNumber(values.mediaIdeal) ?? 0,
  MediaExcelente: parseNumber(values.mediaExcelente) ?? 0,
  PorcentagemDesconto: parseNumber(values.porcentagemDesconto) ?? 0,
  ValorPremiacao: parseNumber(values.valorPremiacao) ?? 0,
  ValorPremiacaoExcelente: parseNumber(values.valorPremiacaoExcelente) ?? 0,
  Ativo: values.ativo === true
});

const PremiacaoTabelaModal = ({ show, onHide }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyTabelaPremiacaoForm);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [showImportacaoModal, setShowImportacaoModal] = useState(false);
  const [numeroFrotaInput, setNumeroFrotaInput] = useState('');
  const [addingVeiculo, setAddingVeiculo] = useState(false);
  const [loadingDeleteVeiculoId, setLoadingDeleteVeiculoId] = useState(null);
  const [showVeiculoSelectionModal, setShowVeiculoSelectionModal] = useState(false);
  const {
    tabelas,
    segmentos,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = usePremiacaoTabelaSelection(show);
  const {
    veiculos,
    loading: veiculosLoading,
    paginaAtual: veiculosPaginaAtual,
    quantidadePorPagina: veiculosQuantidadePorPagina,
    totalRegistros: veiculosTotalRegistros,
    handlePaginationChange: handleVeiculosPaginationChange,
    reload: reloadVeiculos
  } = usePremiacaoTabelaVeiculosVinculados(editingId);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyTabelaPremiacaoForm);
      setLoadingEditId(null);
      setLoadingDeleteId(null);
      setIsFormCollapsed(true);
      setShowImportacaoModal(false);
      setNumeroFrotaInput('');
      setLoadingDeleteVeiculoId(null);
      setShowVeiculoSelectionModal(false);
    }
  }, [show]);

  useEffect(() => {
    setNumeroFrotaInput('');
  }, [editingId]);

  const handleEdit = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const data = await getPremiacaoTabelaById(id);
      setEditingId(id);
      setFormInitialValues(mapApiToFormValues(data));
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao carregar tabela de premiacao');
      console.error('Erro ao carregar tabela de premiacao:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [loadingEditId]);

  const handleDelete = useCallback(async (id) => {
    if (!id || loadingDeleteId === id) return;

    try {
      setLoadingDeleteId(id);
      await deletePremiacaoTabela(id);
      notification.success('Tabela de premiacao excluida com sucesso!');
      if (editingId === id) {
        setEditingId(null);
        setFormInitialValues(emptyTabelaPremiacaoForm);
      }
      reload();
    } catch (error) {
      notification.error('Erro ao excluir tabela de premiacao');
      console.error('Erro ao excluir tabela de premiacao:', error);
    } finally {
      setLoadingDeleteId(null);
    }
  }, [editingId, loadingDeleteId, reload]);

  const handleClear = useCallback((form) => {
    setEditingId(null);
    setFormInitialValues(emptyTabelaPremiacaoForm);
    setNumeroFrotaInput('');
    form.reset(emptyTabelaPremiacaoForm);
    setIsFormCollapsed(false);
  }, []);

  const handleDeleteVeiculo = useCallback(async (id) => {
    if (!id || loadingDeleteVeiculoId === id) return;

    try {
      setLoadingDeleteVeiculoId(id);
      await deletePremiacaoTabelaVeiculo(id);
      notification.success('Vínculo de frota removido com sucesso!');
      reloadVeiculos();
    } catch (error) {
      notification.error('Erro ao remover vinculo de frota');
      console.error('Erro ao remover vinculo de frota:', error);
    } finally {
      setLoadingDeleteVeiculoId(null);
    }
  }, [loadingDeleteVeiculoId, reloadVeiculos]);

  const handleAddVeiculo = useCallback(async () => {
    const numeroFrota = numeroFrotaInput.trim();
    if (!editingId) {
      notification.warning('Salve ou selecione uma tabela de premiacao antes de vincular frotas.');
      return;
    }
    if (!numeroFrota) {
      notification.warning('Informe o numero da frota.');
      return;
    }
    if (addingVeiculo) return;

    try {
      setAddingVeiculo(true);
      await createPremiacaoTabelaVeiculo({
        NumeroFrota: numeroFrota,
        IdTabelaPremiacao: editingId,
        Ativo: true
      });
      notification.success('Frota vinculada com sucesso!');
      setNumeroFrotaInput('');
      reloadVeiculos();
    } catch (error) {
      notification.error('Erro ao vincular frota a tabela');
      console.error('Erro ao vincular frota:', error);
    } finally {
      setAddingVeiculo(false);
    }
  }, [addingVeiculo, editingId, numeroFrotaInput, reloadVeiculos]);

  const handleNumeroFrotaKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddVeiculo();
    }
  }, [handleAddVeiculo]);

  const handleSubmit = useCallback(async (values) => {
    const payload = buildPayload(values);

    try {
      setSaving(true);
      let data;

      if (values.id) {
        data = await updatePremiacaoTabela(values.id, payload);
        notification.success('Tabela de premiacao atualizada com sucesso!');
      } else {
        data = await createPremiacaoTabela(payload);
        notification.success('Tabela de premiacao criada com sucesso!');
      }

      setEditingId(pick(data, 'id', 'Id') || values.id || null);
      setFormInitialValues(mapApiToFormValues(data));
      reload();
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao salvar tabela de premiacao');
      console.error('Erro ao salvar tabela de premiacao:', error);
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const rows = Array.isArray(tabelas) ? tabelas.map(toRowModel) : [];
  const veiculosRows = Array.isArray(veiculos)
    ? veiculos.map((item) => ({
        id: pick(item, 'id', 'Id'),
        numeroFrota: pick(item, 'numeroFrota', 'NumeroFrota') || '',
        ativo: pick(item, 'ativo', 'Ativo') === true,
        data: pick(item, 'data', 'Data'),
        usuarioCriacao: pick(item, 'usuarioCriacao', 'UsuarioCriacao') || ''
      }))
    : [];
  const formKey = useMemo(() => (editingId ? `edit-${editingId}` : 'create'), [editingId]);
  const filterConfig = useMemo(
    () => applyOptionsToFilters(tabelaPremiacaoFilterConfig, {
      Segmento: mapValuesToSelectOptions(segmentos)
    }),
    [segmentos]
  );
  const tabelaContexto = editingId
    ? `${formInitialValues.nome || 'Tabela'} #${editingId}`
    : null;

  return (
    <>
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>Tabelas de Premiação</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={filterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`premiacao-tabela-${paginaAtual}-${quantidadePorPagina}`}
          columns={getTabelaPremiacaoColumns(handleEdit, handleDelete, loadingEditId, loadingDeleteId)}
          data={rows}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
          enableExportExcel={true}
          exportFileName="premiacao-tabelas"
        />

        <CollapsibleCard
          className="mt-3"
          title="Formulário da Tabela de Premiação"
          isCollapsed={isFormCollapsed}
          onToggle={setIsFormCollapsed}
        >
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validate={validateTabelaPremiacaoForm}
          >
            {({ handleSubmit: onFormSubmit, form }) => (
              <FormContainer onSubmit={onFormSubmit}>
                <DynamicForm
                  structure={tabelaPremiacaoFormStructure}
                  sections={tabelaPremiacaoFormSections()}
                  readOnly={false}
                />

                <FormButtonToolbar className="mt-3">
                  <Button variant="secondary" type="button" onClick={() => handleClear(form)} disabled={saving}>
                    Limpar
                  </Button>
                  <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                    Salvar
                  </ButtonWithLoading>
                </FormButtonToolbar>
              </FormContainer>
            )}
          </Form>
        </CollapsibleCard>

        {editingId ? (
          <CollapsibleCard
            className="mt-3"
            title={`Veículos${tabelaContexto ? ` - ${tabelaContexto}` : ''}`}
            isCollapsed={false}
          >
            <TanStackTable
              key={`premiacao-tabela-veiculos-${editingId}-${veiculosPaginaAtual}-${veiculosQuantidadePorPagina}`}
              columns={getTabelaVeiculoVinculoColumns(handleDeleteVeiculo, loadingDeleteVeiculoId)}
              data={veiculosRows}
              loading={veiculosLoading}
              withPagination={true}
              pageSizeOptions={[5, 10, 25, 50]}
              enableRowColors={true}
              manualPagination={true}
              totalItems={veiculosTotalRegistros}
              controlledPagination={{
                pageIndex: Math.max(0, (veiculosPaginaAtual || 1) - 1),
                pageSize: veiculosQuantidadePorPagina || 10
              }}
              onPaginationChange={handleVeiculosPaginationChange}
            />

            <div className="d-flex align-items-end gap-2 mt-3 flex-wrap">
              <BootstrapForm.Group className="mb-0" style={{ minWidth: 220 }}>
                <BootstrapForm.Label htmlFor="premiacao-numero-frota">Numero Frota</BootstrapForm.Label>
                <div className="d-flex gap-2">
                  <BootstrapForm.Control
                    id="premiacao-numero-frota"
                    type="text"
                    value={numeroFrotaInput}
                    onChange={(event) => setNumeroFrotaInput(event.target.value)}
                    onKeyDown={handleNumeroFrotaKeyDown}
                    placeholder="Informe o numero da frota"
                    disabled={addingVeiculo}
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowVeiculoSelectionModal(true)}
                    disabled={addingVeiculo}
                    title="Selecionar veículos próprios"
                  >
                    Buscar
                  </Button>
                </div>
              </BootstrapForm.Group>
              <ButtonWithLoading
                variant="primary"
                type="button"
                onClick={handleAddVeiculo}
                loading={addingVeiculo}
                disabled={!numeroFrotaInput.trim()}
              >
                Adicionar
              </ButtonWithLoading>
            </div>
          </CollapsibleCard>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {totalRegistros > 0 && (
              <span>
                Exibindo {rows.length} de {totalRegistros} tabela(s)
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => setShowImportacaoModal(true)}>
              Importar Tabelas
            </Button>
            <Button variant="secondary" onClick={onHide}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>

    <PremiacaoVeiculoProprioSelectionModal
      show={showVeiculoSelectionModal}
      onHide={() => setShowVeiculoSelectionModal(false)}
      idTabelaPremiacao={editingId}
      onSuccess={reloadVeiculos}
    />

    <PremiacaoImportacaoExcelModal
      show={showImportacaoModal}
      onClose={() => setShowImportacaoModal(false)}
      onImportado={reload}
      title="Importar Tabelas de Premiação"
      layoutColumns={LAYOUT_IMPORTACAO_TABELAS.columns}
      layoutObservacoes={LAYOUT_IMPORTACAO_TABELAS.observacoes}
      layoutFileName={LAYOUT_IMPORTACAO_TABELAS.fileName}
      layoutFileNameComRegistros={LAYOUT_IMPORTACAO_TABELAS.fileNameComRegistros}
      onImport={importarPremiacaoTabelas}
      onDownloadLayoutComRegistros={exportarLayoutImportacaoPremiacaoTabelasComRegistros}
    />
    </>
  );
};

export default PremiacaoTabelaModal;
